/**
 * TAG Heuer Inquiry Form Module
 * 
 * Step 1: Watch information (Collection, Reference, Price, Availability, Store Location)
 * Step 2: Contact information (First/Last name, Email, Phone [optional])
 * 
 * Google Sheet expected columns:
 *   A: Collection | B: Reference | C: CAD (Price) | D: Availability | E: Store Location
 * 
 * Conversion tracking events:
 *   - th_form_view         → form is visible / loaded
 *   - th_step1_filled      → user fills watch fields
 *   - th_step1_next        → user clicks Next (passes step 1 validation)
 *   - th_step2_filled      → user fills contact fields
 *   - th_form_submit       → user clicks Send Inquiry (successful submission)
 * 
 * Environment variable required:
 *   GOOGLE_SHEETS_API_KEY – for public Google Sheets API access
 */

// ------------------------------------------
// Email & Notification Configuration
// ------------------------------------------
// Update these values to control email routing without touching form logic.

var TAG_HEUER_EMAIL_CONFIG = {
  // Internal notification
  internalRecipient: 'onlinesupport@raffiandco.com',
  internalSubject: 'TAG Heuer Inquiry – Website',

  // Customer confirmation
  senderName: 'Raffi Jewellers',
  replyTo: 'onlinesupport@raffiandco.com',
  customerSubject: 'Your TAG Heuer Inquiry – Raffi Jewellers',
  customerClosing: 'Warm regards,\nRaffi Jewellers',

  // Submission context (included in backend payload)
  formContext: 'TAG Heuer Inquiry – Collection Page',
  formType: 'tag-heuer-inquiry',
};

interface TagHeuerSKU {
  collection: string;
  reference: string;
  referenceNormalized: string;
  /** Optional display title (sheet column C), shown before the reference */
  title: string;
  price: string;
  availability: string;
  storeLocation: string;
  /** Optional image URL (sheet column F) shown beside the reference */
  image: string;
  /** Optional Shopify product handle (sheet column G) — when present, a
   *  successful inquiry redirects to that (unlisted) product page */
  handle: string;
}

class TagHeuerInquiryForm {
  private form: HTMLFormElement | null = null;
  private section: HTMLElement | null = null;
  private wrapper: HTMLElement | null = null;
  private stickyPlaceholder: HTMLElement | null = null;
  private step1: HTMLElement | null = null;
  private step2: HTMLElement | null = null;
  private successStep: HTMLElement | null = null;
  private skuData: TagHeuerSKU[] = [];
  private sheetId: string = '';

  constructor() {
    var section = document.querySelector('.th-inquiry') as HTMLElement;
    if (!section) return;

    this.section = section;
    this.wrapper = section.closest('.th-inquiry-wrapper') as HTMLElement;
    this.form = document.getElementById('th-inquiry-form') as HTMLFormElement;
    this.step1 = section.querySelector('[data-step="1"]') as HTMLElement;
    this.step2 = section.querySelector('[data-step="2"]') as HTMLElement;
    this.successStep = section.querySelector('[data-step="success"]') as HTMLElement;
    this.sheetId = section.dataset.sheetId || '';

    // Override config from data attributes if provided in Contentful
    var sendTo = section.dataset.sendTo;
    var sendFrom = section.dataset.sendFrom;
    if (sendTo) TAG_HEUER_EMAIL_CONFIG.internalRecipient = sendTo;
    if (sendFrom) TAG_HEUER_EMAIL_CONFIG.senderName = sendFrom;

    this.init();
  }

  private async init() {
    this.bindNavigation();
    this.bindSubmit();
    this.bindInteractionTracking();
    this.initStickyPinning();

    if (this.sheetId) {
      await this.loadSheetData();
      this.populateCollections();
      this.bindAutosuggest();
      this.bindRefDropdownToggle();
      this.bindCollectionChange();
    }

    // Track form view
    this.trackEvent('th_form_view');
  }

  /**
   * Track focus/blur on all form inputs to set isInteracting flag.
   * When interacting, the mobile sticky header will not auto-collapse.
   */
  private bindInteractionTracking() {
    if (!this.section) return;
    var self = this;

    // Track whether a form field is actively focused
    this.section.addEventListener('focusin', function() {
      self.isFocused = true;
      self.isFocused = true;
      // If form is collapsed while user focuses, auto-expand
      if (self.isMobileViewport() && self.mobileCollapsed && self.section!.classList.contains('th-inquiry--fixed')) {
        self.mobileCollapsed = false;
        self.mobileExpandedWhileSticky = true;
        self.expandScrollY = window.scrollY;
        self.section!.classList.remove('th-inquiry--mobile-collapsed');
        self.section!.classList.add('th-inquiry--mobile-expanded');
      }
    });

    this.section.addEventListener('focusout', function() {
      // Delay to allow focus to move to another field within the form
      setTimeout(function() {
        if (!self.section) return;
        var activeEl = document.activeElement;
        var stillInForm = self.section.contains(activeEl);
        // Also check if suggestions dropdown is focused (may be inside form on mobile)
        var suggestionsEl = document.getElementById('th-suggestions');
        var inSuggestions = suggestionsEl && suggestionsEl.contains(activeEl);
        if (!stillInForm && !inSuggestions) {
          self.isFocused = false;
        }
      }, 150);
    });
  }

  private mobileCollapsed: boolean = false;
  private mobileExpandedWhileSticky: boolean = false;
  private collapsedHeader: HTMLElement | null = null;
  private isFocused: boolean = false;
  private expandScrollY: number = 0;

  private isMobileViewport(): boolean {
    return window.innerWidth < 768;
  }

  private initStickyPinning() {
    if (!this.section || !this.wrapper || !this.section.classList.contains('th-inquiry--sticky')) return;

    var section = this.section;
    var placeholder = document.createElement('div');
    var self = this;
    var ticking = false;

    placeholder.className = 'th-inquiry__sticky-placeholder';
    placeholder.style.display = 'block';
    placeholder.style.width = '100%';
    placeholder.style.height = '0px';
    placeholder.style.overflow = 'hidden';
    section.insertAdjacentElement('beforebegin', placeholder);
    this.stickyPlaceholder = placeholder;

    // Mobile collapsed header setup
    this.collapsedHeader = section.querySelector('#th-collapsed-header') as HTMLElement;
    if (this.collapsedHeader) {
      this.collapsedHeader.addEventListener('click', function() {
        self.toggleMobileCollapse();
      });
    }

    function resetState() {
      section.classList.remove('th-inquiry--fixed');
      section.classList.remove('th-inquiry--stopped');
      section.classList.remove('th-inquiry--mobile-collapsed');
      section.classList.remove('th-inquiry--mobile-expanded');
      section.style.left = '';
      section.style.width = '';
      section.style.top = '';
      self.mobileCollapsed = false;
      self.mobileExpandedWhileSticky = false;
      if (self.stickyPlaceholder) {
        self.stickyPlaceholder.style.height = '0px';
      }
    }

    function updateStickyState() {
      if (!self.section || !self.stickyPlaceholder) return;

      var isPinned = section.classList.contains('th-inquiry--fixed');
      var isMobile = self.isMobileViewport();

      // For height calculation, use stored height when collapsed on mobile
      var sectionHeight: number;
      if (isPinned && isMobile && self.mobileCollapsed) {
        sectionHeight = parseInt(self.stickyPlaceholder.dataset.fullHeight || '0', 10) || section.offsetHeight;
      } else if (isPinned) {
        sectionHeight = parseInt(self.stickyPlaceholder.style.height, 10) || section.offsetHeight;
      } else {
        sectionHeight = section.offsetHeight;
      }

      var placeholderRect = self.stickyPlaceholder.getBoundingClientRect();
      var shouldPin = placeholderRect.top < 0;

      if (!shouldPin) {
        resetState();
        return;
      }

      // Store full height before collapsing
      if (!isPinned) {
        self.stickyPlaceholder.dataset.fullHeight = sectionHeight + '';
      }

      self.stickyPlaceholder.style.height = sectionHeight + 'px';
      section.style.left = placeholderRect.left + 'px';
      section.style.width = placeholderRect.width + 'px';
      section.classList.add('th-inquiry--fixed');
      section.style.top = '0px';

      // On mobile, auto-collapse when first pinned (unless user is focused on a field)
      if (isMobile && !isPinned && !self.isFocused) {
        self.mobileCollapsed = true;
        self.mobileExpandedWhileSticky = false;
        section.classList.add('th-inquiry--mobile-collapsed');
        section.classList.remove('th-inquiry--mobile-expanded');
      }

      // On mobile, if user manually expanded but then scrolls 120px+ further down, re-collapse
      // Only if no field is actively focused
      if (isMobile && isPinned && self.mobileExpandedWhileSticky && !self.isFocused) {
        var scrollDelta = Math.abs(window.scrollY - self.expandScrollY);
        if (scrollDelta > 120) {
          self.mobileCollapsed = true;
          self.mobileExpandedWhileSticky = false;
          section.classList.add('th-inquiry--mobile-collapsed');
          section.classList.remove('th-inquiry--mobile-expanded');
        }
      }
    }

    function requestTick() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function() {
        updateStickyState();
        ticking = false;
      });
    }

    window.addEventListener('scroll', requestTick, { passive: true });
    window.addEventListener('resize', requestTick);
    window.addEventListener('load', requestTick);

    requestTick();
  }

  private toggleMobileCollapse() {
    if (!this.section) return;

    if (this.mobileCollapsed) {
      // Expand
      this.mobileCollapsed = false;
      this.mobileExpandedWhileSticky = true;
      this.expandScrollY = window.scrollY; // Track scroll position at expand time
      this.section.classList.remove('th-inquiry--mobile-collapsed');
      this.section.classList.add('th-inquiry--mobile-expanded');
    } else {
      // Collapse
      this.mobileCollapsed = true;
      this.mobileExpandedWhileSticky = false;
      this.section.classList.add('th-inquiry--mobile-collapsed');
      this.section.classList.remove('th-inquiry--mobile-expanded');
    }
  }

  // ------------------------------------------
  // Conversion tracking
  // ------------------------------------------
  private trackEvent(eventName: string, params: Record<string, string> = {}) {
    try {
      if (typeof (window as any).gtag === 'function') {
        (window as any).gtag('event', eventName, {
          event_category: 'TAG Heuer Inquiry',
          ...params,
        });
        return;
      }

      if (typeof (window as any).dataLayer !== 'undefined') {
        (window as any).dataLayer.push({
          event: eventName,
          event_category: 'TAG Heuer Inquiry',
          ...params,
        });
        return;
      }

      console.log('[TH Tracking] ' + eventName, params);
    } catch (err) {
      console.warn('Tracking error:', err);
    }
  }

  // ------------------------------------------
  // Google Sheets data loading
  // ------------------------------------------
  private async loadSheetData() {
    try {
      var response = await fetch('/api/tag-heuer/skus?sheetId=' + this.sheetId);
      if (!response.ok) throw new Error('Failed to load SKU data');

      var data = await response.json();

      // Map columns BY HEADER NAME so the sheet can be reorganised (e.g.
      // inserting a Title column) without breaking the form. Falls back to
      // the original positional layout if a header is missing.
      var rows: string[][] = data.values || [];
      var header = (rows[0] || []).map(function(h) { return String(h || '').trim().toLowerCase(); });
      var col = function(name: string, fallback: number) {
        var i = header.indexOf(name);
        return i === -1 ? fallback : i;
      };
      var idx = {
        collection: col('collection', 0),
        reference: col('reference', 1),
        title: col('title', -1),
        price: col('cad', 2),
        availability: col('availability', 3),
        storeLocation: col('store location', 4),
        image: col('image', -1),
        handle: col('handle', -1),
      };
      var cell = function(row: string[], i: number) {
        return i === -1 ? '' : (row[i] || '').trim();
      };

      var self = this;
      this.skuData = rows
        .slice(1)
        .filter(function(row: string[]) { return cell(row, idx.collection) && cell(row, idx.reference); })
        // Only in-stock watches are offered (Availability = Yes); anything
        // typed that isn't in the list still gets the contact-us fallback
        .filter(function(row: string[]) { return cell(row, idx.availability).toLowerCase() === 'yes'; })
        .map(function(row: string[]) {
          return {
            collection: cell(row, idx.collection),
            reference: cell(row, idx.reference),
            referenceNormalized: self.normalize(cell(row, idx.reference)),
            title: cell(row, idx.title),
            price: self.formatPrice(cell(row, idx.price)),
            availability: cell(row, idx.availability),
            storeLocation: cell(row, idx.storeLocation),
            image: cell(row, idx.image),
            handle: cell(row, idx.handle),
          };
        });
    } catch (err) {
      console.warn('TAG Heuer SKU data could not be loaded:', err);
    }
  }

  private normalize(str: string): string {
    return str.toUpperCase().replace(/[\s\-\.]/g, '');
  }

  private formatPrice(raw: string): string {
    if (!raw || raw === '#N/A') return '';
    if (raw.startsWith('$')) return raw;
    var num = parseFloat(raw.replace(/,/g, ''));
    if (isNaN(num)) return raw;
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(num);
  }

  private formatAvailability(sku: TagHeuerSKU): string {
    var avail = sku.availability.toLowerCase();
    var location = sku.storeLocation.trim();

    if (!avail || avail === 'no') {
      return 'Contact us for availability';
    }

    if (avail === 'yes' && location) {
      var locations = location.split(',').map(function(l) { return l.trim(); }).filter(Boolean);
      if (locations.length === 1) {
        return 'Available in ' + locations[0];
      } else if (locations.length === 2) {
        return 'Available in ' + locations[0] + ' and ' + locations[1];
      } else if (locations.length > 2) {
        var last = locations.pop();
        return 'Available in ' + locations.join(', ') + ', and ' + last;
      }
    }

    if (avail === 'yes') {
      return 'Available \u2013 contact us for details';
    }

    return sku.availability;
  }

  private getAvailabilityRank(sku: TagHeuerSKU): number {
    var avail = sku.availability.toLowerCase();
    var location = sku.storeLocation.trim();

    // Tier 1: Available with specific store locations
    if (avail === 'yes' && location) return 0;
    // Tier 2: Available / in stock (generic)
    if (avail === 'yes') return 1;
    // Tier 3: Has some availability text that isn't 'no' or empty
    if (avail && avail !== 'no') return 2;
    // Tier 4: Unavailable / contact us
    return 3;
  }

  private sortByAvailability(items: TagHeuerSKU[]): TagHeuerSKU[] {
    var self = this;
    return items.slice().sort(function(a, b) {
      var rankA = self.getAvailabilityRank(a);
      var rankB = self.getAvailabilityRank(b);
      if (rankA !== rankB) return rankA - rankB;
      return a.reference.localeCompare(b.reference);
    });
  }

  // ------------------------------------------
  // Populate collection dropdown
  // ------------------------------------------
  private populateCollections() {
    var select = document.getElementById('th-collection') as HTMLSelectElement;
    if (!select) return;

    var collections = [...new Set(this.skuData.map(function(s) { return s.collection; }))].sort();
    collections.forEach(function(col) {
      var opt = document.createElement('option');
      opt.value = col;
      opt.textContent = col;
      select.appendChild(opt);
    });
  }

  // ------------------------------------------
  // Reference number autosuggest + dropdown
  // ------------------------------------------
  private bindAutosuggest() {
    var input = document.getElementById('th-modelNumber') as HTMLInputElement;
    var suggestionsEl = document.getElementById('th-suggestions') as HTMLElement;
    var collectionSelect = document.getElementById('th-collection') as HTMLSelectElement;
    if (!input || !suggestionsEl) return;

    var inputWrap = input.closest('.th-inquiry__input-wrap') || input.parentElement;

    // The dropdown is always anchored inside the input wrapper (absolute,
    // below the input). The old fixed-position body portal went stale the
    // moment the sticky form pinned and the page scrolled, and lost stacking
    // fights with the TAG Heuer iframe loader — the section's z-index (raised
    // above the iframe overlay in CSS) keeps the list on top instead.
    if (inputWrap && suggestionsEl.parentElement !== inputWrap) {
      (inputWrap as HTMLElement).appendChild(suggestionsEl);
    }

    var activeIndex = -1;
    var self = this;

    input.addEventListener('focus', function() {
      if (suggestionsEl.classList.contains('th-inquiry__suggestions--visible')) return;
      var selectedCollection = collectionSelect ? collectionSelect.value : '';
      // Show the full (collection-filtered) list — the input may hold a
      // previous selection's display string, which is not a search query
      self.showFilteredReferences(selectedCollection, '', suggestionsEl, input);
      self.updateToggleState(true);
    });

    input.addEventListener('input', function() {
      var query = self.normalize(input.value);
      var selectedCollection = collectionSelect ? collectionSelect.value : '';

      if (query.length < 1) {
        self.showFilteredReferences(selectedCollection, '', suggestionsEl, input);
        return;
      }

      var matches = self.skuData.filter(function(s) {
        var collectionMatch = !selectedCollection || s.collection === selectedCollection;
        return collectionMatch
          && (s.referenceNormalized.includes(query) || self.normalize(s.title).includes(query));
      });

      matches = self.sortByAvailability(matches);

      if (matches.length === 0) {
        self.hideSuggestions(suggestionsEl);
        self.showFallback();
        self.clearAutoFill();
        return;
      }

      self.hideFallback();
      activeIndex = -1;
      self.renderSuggestions(matches, suggestionsEl, input);
    });

    input.addEventListener('keydown', function(e) {
      var items = suggestionsEl.querySelectorAll('.th-inquiry__suggestion-item');
      if (!items.length) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        activeIndex = Math.min(activeIndex + 1, items.length - 1);
        self.highlightSuggestion(items, activeIndex);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        activeIndex = Math.max(activeIndex - 1, 0);
        self.highlightSuggestion(items, activeIndex);
      } else if (e.key === 'Enter' && activeIndex >= 0) {
        e.preventDefault();
        var ref = (items[activeIndex] as HTMLElement).dataset.ref || '';
        self.selectReference(ref, input, suggestionsEl);
      } else if (e.key === 'Escape') {
        self.hideSuggestions(suggestionsEl);
      }
    });

    input.addEventListener('focus', function() {
      var selectedCollection = collectionSelect ? collectionSelect.value : '';
      var query = self.normalize(input.value);
      if (query.length < 2) {
        self.showFilteredReferences(selectedCollection, query, suggestionsEl, input);
      }
    });

    document.addEventListener('click', function(e) {
      var target = e.target as HTMLElement;
      var clickedInsideField = !!target.closest('.th-inquiry__field--suggest');
      var clickedInsideSuggestions = suggestionsEl.contains(target);

      if (!clickedInsideField && !clickedInsideSuggestions) {
        self.hideSuggestions(suggestionsEl);
        self.updateToggleState(false);
      }
    });
  }

  private showFilteredReferences(collection: string, query: string, suggestionsEl: HTMLElement, input: HTMLInputElement) {
    var matches = collection
      ? this.skuData.filter(function(s) { return s.collection === collection; })
      : this.skuData.slice();
    if (query) {
      var norm = this.normalize(query);
      var self2 = this;
      matches = matches.filter(function(s) {
        return s.referenceNormalized.includes(norm) || self2.normalize(s.title).includes(norm);
      });
    }
    matches = this.sortByAvailability(matches);

    if (matches.length === 0) {
      this.hideSuggestions(suggestionsEl);
      if (query) this.showFallback();
      return;
    }

    this.hideFallback();
    this.renderSuggestions(matches, suggestionsEl, input);
    this.updateToggleState(true);
  }

  private renderSuggestions(matches: TagHeuerSKU[], suggestionsEl: HTMLElement, input: HTMLInputElement) {
    var self = this;
    suggestionsEl.innerHTML = matches
      .map(function(m, i) {
        // Stacked layout: Title / Reference + availability (small, availability
        // in green when in stock) / Price. Rows without a Title lead with the
        // reference instead.
        var inStock = m.availability.toLowerCase() === 'yes';
        var availHtml = '<span class="th-inquiry__suggestion-availability'
          + (inStock ? ' th-inquiry__suggestion-availability--in-stock' : '')
          + '">' + self.formatAvailability(m) + '</span>';
        var titleLine = '<span class="th-inquiry__suggestion-title">' + (m.title || m.reference) + '</span>';
        var metaLine = '<span class="th-inquiry__suggestion-meta">'
          + (m.title ? '<span class="th-inquiry__suggestion-ref">' + m.reference + '</span>' : '')
          + availHtml
          + '</span>';
        var priceLine = m.price ? '<span class="th-inquiry__suggestion-price">' + m.price + '</span>' : '';
        var thumb = m.image
          ? '<img class="th-inquiry__suggestion-thumb" src="' + m.image + '" alt="" loading="lazy" onerror="this.remove()" />'
          : '';
        return '<div class="th-inquiry__suggestion-item' + (m.image ? ' th-inquiry__suggestion-item--with-thumb' : '') + '" data-index="' + i + '" data-ref="' + m.reference + '">'
          + thumb
          + '<span class="th-inquiry__suggestion-label">' + titleLine + metaLine + priceLine + '</span>'
          + '</div>';
      })
      .join('');

    // Ensure the dropdown is anchored inside the input wrapper — CSS handles
    // all positioning (absolute, full input width, directly below the field)
    var inputWrap = input.closest('.th-inquiry__input-wrap') || input;
    if (inputWrap && suggestionsEl.parentElement !== inputWrap) {
      (inputWrap as HTMLElement).appendChild(suggestionsEl);
    }
    suggestionsEl.style.top = '';
    suggestionsEl.style.left = '';
    suggestionsEl.style.width = '';
    suggestionsEl.dataset.theme = this.section && this.section.classList.contains('th-inquiry--light') ? 'light' : 'dark';

    suggestionsEl.classList.add('th-inquiry__suggestions--visible');

    suggestionsEl.querySelectorAll('.th-inquiry__suggestion-item').forEach(function(item) {
      item.addEventListener('click', function() {
        var ref = (item as HTMLElement).dataset.ref || '';
        self.selectReference(ref, input, suggestionsEl);
        self.updateToggleState(false);
      });
    });
  }

  private bindRefDropdownToggle() {
    var toggle = document.getElementById('th-ref-toggle');
    var input = document.getElementById('th-modelNumber') as HTMLInputElement;
    var suggestionsEl = document.getElementById('th-suggestions') as HTMLElement;
    var collectionSelect = document.getElementById('th-collection') as HTMLSelectElement;
    if (!toggle || !input || !suggestionsEl) return;

    var self = this;
    toggle.addEventListener('click', function() {
      var isOpen = suggestionsEl.classList.contains('th-inquiry__suggestions--visible');
      if (isOpen) {
        self.hideSuggestions(suggestionsEl);
        self.updateToggleState(false);
      } else {
        var selectedCollection = collectionSelect ? collectionSelect.value : '';
        self.showFilteredReferences(selectedCollection, '', suggestionsEl, input);
        input.focus();
      }
    });
  }

  private bindCollectionChange() {
    var collectionSelect = document.getElementById('th-collection') as HTMLSelectElement;
    var input = document.getElementById('th-modelNumber') as HTMLInputElement;
    var suggestionsEl = document.getElementById('th-suggestions') as HTMLElement;
    if (!collectionSelect || !input || !suggestionsEl) return;

    var self = this;
    collectionSelect.addEventListener('change', function() {
      input.value = '';
      self.clearAutoFill();
      self.hideFallback();
      var col = collectionSelect.value;
      if (col) {
        self.showFilteredReferences(col, '', suggestionsEl, input);
        input.focus();
      }
    });
  }

  private updateToggleState(isOpen: boolean) {
    var toggle = document.getElementById('th-ref-toggle');
    if (toggle) {
      toggle.classList.toggle('th-inquiry__ref-dropdown-toggle--open', isOpen);
    }
  }

  private selectReference(reference: string, input: HTMLInputElement, suggestionsEl: HTMLElement) {
    var sku = this.skuData.find(function(s) { return s.reference === reference; });
    if (sku) {
      var titleStr = sku.title ? sku.title + ' \u2013 ' : '';
      var priceStr = sku.price ? ' \u2013 ' + sku.price : '';
      var availStr = ' \u2013 ' + this.formatAvailability(sku);
      input.value = titleStr + reference + priceStr + availStr;
    } else {
      input.value = reference;
    }
    this.hideSuggestions(suggestionsEl);

    if (sku) {
      this.autoFill(sku);
      this.hideFallback();

      var collectionSelect = document.getElementById('th-collection') as HTMLSelectElement;
      if (collectionSelect && sku.collection) {
        collectionSelect.value = sku.collection;
      }

      this.trackEvent('th_step1_filled', {
        collection: sku.collection,
        reference: sku.reference,
      });
    } else {
      this.clearAutoFill();
      this.showFallback();
    }
  }

  private autoFill(sku: TagHeuerSKU) {
    var priceEl = document.getElementById('th-price') as HTMLInputElement;
    var availEl = document.getElementById('th-availability') as HTMLInputElement;
    var storeEl = document.getElementById('th-storeLocation') as HTMLInputElement;

    if (priceEl) priceEl.value = sku.price;
    if (availEl) availEl.value = this.formatAvailability(sku);
    if (storeEl) storeEl.value = sku.storeLocation || '';
  }

  private clearAutoFill() {
    var priceEl = document.getElementById('th-price') as HTMLInputElement;
    var availEl = document.getElementById('th-availability') as HTMLInputElement;
    var storeEl = document.getElementById('th-storeLocation') as HTMLInputElement;
    if (priceEl) priceEl.value = '';
    if (availEl) availEl.value = '';
    if (storeEl) storeEl.value = '';
  }

  private highlightSuggestion(items: NodeListOf<Element>, index: number) {
    items.forEach(function(i) { i.classList.remove('th-inquiry__suggestion-item--active'); });
    if (items[index]) items[index].classList.add('th-inquiry__suggestion-item--active');
  }

  private hideSuggestions(el: HTMLElement) {
    el.classList.remove('th-inquiry__suggestions--visible');
    el.innerHTML = '';
  }

  private showFallback() {
    var el = document.getElementById('th-fallback');
    if (el) el.style.display = 'block';
  }

  private hideFallback() {
    var el = document.getElementById('th-fallback');
    if (el) el.style.display = 'none';
  }

  // ------------------------------------------
  // Step navigation
  // ------------------------------------------
  private bindNavigation() {
    var self = this;
    var nextBtn = document.getElementById('th-next-btn');
    var backBtn = document.getElementById('th-back-btn');
    var restartBtn = document.getElementById('th-restart-btn');

    if (nextBtn) {
      nextBtn.addEventListener('click', function() {
        if (self.validateStep1()) {
          self.trackEvent('th_step1_next', {
            collection: (document.getElementById('th-collection') as HTMLSelectElement).value || '',
            reference: (document.getElementById('th-modelNumber') as HTMLInputElement).value || '',
          });
          self.goToStep(2);
        }
      });
    }

    if (backBtn) {
      backBtn.addEventListener('click', function() {
        self.goToStep(1);
      });
    }

    if (restartBtn) {
      restartBtn.addEventListener('click', function() {
        self.resetForm();
      });
    }
  }

  private resetForm() {
    // Reset the HTML form (clears all inputs)
    if (this.form) this.form.reset();

    // Clear autofill fields (hidden + readonly)
    this.clearAutoFill();

    // Reset collection select to default placeholder
    var collection = document.getElementById('th-collection') as HTMLSelectElement;
    if (collection) collection.selectedIndex = 0;

    // Clear reference input
    var model = document.getElementById('th-modelNumber') as HTMLInputElement;
    if (model) model.value = '';

    // Clear availability
    var avail = document.getElementById('th-availability') as HTMLInputElement;
    if (avail) avail.value = '';

    // Clear contact fields
    var fields = ['th-firstName', 'th-lastName', 'th-email', 'th-phone'];
    fields.forEach(function(id) {
      var el = document.getElementById(id) as HTMLInputElement;
      if (el) el.value = '';
    });

    // Clear all error states
    if (this.form) {
      this.form.querySelectorAll('.th-inquiry__error').forEach(function(el) {
        (el as HTMLElement).textContent = '';
      });
      this.form.querySelectorAll('.th-inquiry__input--invalid, .th-inquiry__select--invalid').forEach(function(el) {
        el.classList.remove('th-inquiry__input--invalid', 'th-inquiry__select--invalid');
      });
    }

    // Hide fallback and submit error
    this.hideFallback();
    var submitErr = document.getElementById('th-submit-error');
    if (submitErr) submitErr.style.display = 'none';

    // Go back to step 1
    this.goToStep(1);
  }

  private goToStep(step: number) {
    var steps = [this.step1, this.step2, this.successStep];
    for (var i = 0; i < steps.length; i++) {
      if (steps[i]) steps[i]!.classList.remove('th-inquiry__step--active');
    }

    if (step === 1 && this.step1) this.step1.classList.add('th-inquiry__step--active');
    else if (step === 2 && this.step2) this.step2.classList.add('th-inquiry__step--active');
    else if (step === 3 && this.successStep) this.successStep.classList.add('th-inquiry__step--active');

    // On mobile step transitions, keep form expanded and prevent re-collapse
    if (this.isMobileViewport() && this.section) {
      this.isFocused = true;
      this.mobileExpandedWhileSticky = true;
      this.expandScrollY = window.scrollY;
      this.mobileCollapsed = false;
      this.section.classList.remove('th-inquiry--mobile-collapsed');
      // Only add expanded class if we're in fixed/sticky mode
      if (this.section.classList.contains('th-inquiry--fixed')) {
        this.section.classList.add('th-inquiry--mobile-expanded');
      }
      // Reset isFocused after a delay to allow re-collapse on scroll
      var self = this;
      setTimeout(function() { self.isFocused = false; }, 500);
    }

    if (this.form) this.form.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // ------------------------------------------
  // Validation
  // ------------------------------------------
  private validateStep1(): boolean {
    var valid = true;

    var collection = document.getElementById('th-collection') as HTMLSelectElement;
    var collErr = this.form ? this.form.querySelector('[data-error="collection"]') as HTMLElement : null;
    if (collection && collErr) {
      collection.classList.remove('th-inquiry__select--invalid');
      collErr.textContent = '';
      if (!collection.value) {
        collection.classList.add('th-inquiry__select--invalid');
        collErr.textContent = 'Collection is required';
        valid = false;
      }
    }

    var model = document.getElementById('th-modelNumber') as HTMLInputElement;
    var modelErr = this.form ? this.form.querySelector('[data-error="modelNumber"]') as HTMLElement : null;
    if (model && modelErr) {
      model.classList.remove('th-inquiry__input--invalid');
      modelErr.textContent = '';
      if (!model.value.trim()) {
        model.classList.add('th-inquiry__input--invalid');
        modelErr.textContent = 'Reference number is required';
        valid = false;
      }
    }

    return valid;
  }

  private validateStep2(): boolean {
    // Only firstName, lastName, email are required. Phone is optional.
    var requiredFields = [
      { name: 'firstName', label: 'First name' },
      { name: 'lastName', label: 'Last name' },
      { name: 'email', label: 'Email address' },
    ];

    var valid = true;
    var self = this;

    requiredFields.forEach(function(field) {
      var input = self.form ? self.form.querySelector('[name="' + field.name + '"]') as HTMLInputElement : null;
      var errorEl = self.form ? self.form.querySelector('[data-error="' + field.name + '"]') as HTMLElement : null;

      if (!input || !errorEl) return;

      input.classList.remove('th-inquiry__input--invalid');
      errorEl.textContent = '';

      if (!input.value.trim()) {
        input.classList.add('th-inquiry__input--invalid');
        errorEl.textContent = field.label + ' is required';
        valid = false;
      } else if (field.name === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.value.trim())) {
        input.classList.add('th-inquiry__input--invalid');
        errorEl.textContent = 'Please enter a valid email address';
        valid = false;
      }
    });

    // Clear phone error (it's optional now)
    var phoneErr = self.form ? self.form.querySelector('[data-error="phone"]') as HTMLElement : null;
    if (phoneErr) phoneErr.textContent = '';

    if (valid) {
      this.trackEvent('th_step2_filled');
    }

    return valid;
  }

  // ------------------------------------------
  // Form submission
  // ------------------------------------------
  private bindSubmit() {
    var self = this;
    if (!this.form) return;

    this.form.addEventListener('submit', function(e) {
      e.preventDefault();

      if (!self.validateStep2()) return;

      var submitBtn = document.getElementById('th-submit-btn') as HTMLButtonElement;
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.classList.add('th-inquiry__btn--loading');
      }

      var formData = new FormData(self.form!);
      var rawModel = (formData.get('modelNumber') as string) || '';
      // The visible value is "Title \u2013 Reference \u2013 Price \u2013 Availability" —
      // recover the actual reference by finding a known SKU inside it
      var matchedSku = self.skuData.find(function(s) { return rawModel.indexOf(s.reference) !== -1; });
      var modelNumber = matchedSku ? matchedSku.reference : rawModel.split(' \u2013 ')[0].trim();
      var emailVariant = !!(self.section && self.section.dataset.emailVariant);
      var payload = {
        firstName: formData.get('firstName'),
        lastName: formData.get('lastName'),
        email: formData.get('email'),
        phone: formData.get('phone') || '',
        collection: formData.get('collection'),
        modelNumber: modelNumber,
        price: formData.get('price') || 'Not available',
        availability: formData.get('availability') || 'Not available',
        storeLocation: formData.get('storeLocation') || '',
        handle: (matchedSku && matchedSku.handle) || '',
        emailProductLink: emailVariant,
        context: TAG_HEUER_EMAIL_CONFIG.formContext,
        formType: TAG_HEUER_EMAIL_CONFIG.formType,
        // Email configuration passed to the backend
        sendToEmail: TAG_HEUER_EMAIL_CONFIG.internalRecipient,
        sendFromName: TAG_HEUER_EMAIL_CONFIG.senderName,
        replyTo: TAG_HEUER_EMAIL_CONFIG.replyTo,
        internalSubject: TAG_HEUER_EMAIL_CONFIG.internalSubject,
        customerSubject: TAG_HEUER_EMAIL_CONFIG.customerSubject,
      };

      var csrfMeta = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement;

      fetch('/api/forms/tag-heuer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfMeta ? csrfMeta.content : '',
        },
        body: JSON.stringify(payload),
      })
      .then(function(response) {
        if (!response.ok) throw new Error('Submission failed (' + response.status + ')');
        return response.json();
      })
      .then(function(result) {
        self.trackEvent('th_form_submit', {
          collection: payload.collection as string,
          reference: payload.modelNumber as string,
        });

        // Show the luxury confirmation state (no alert popup)
        self.goToStep(3);

        var successInner = document.querySelector('.th-inquiry__success-inner');
        var addNote = function(text: string) {
          if (!successInner) return;
          var note = document.createElement('p');
          note.className = 'th-inquiry__success-redirect';
          note.textContent = text;
          successInner.appendChild(note);
        };

        if (emailVariant) {
          // Email variant: the tokenized product link arrives by email — no redirect
          if (matchedSku && matchedSku.handle) {
            addNote('A link to the details of the ' + matchedSku.reference + ' is on its way to your inbox.');
          }
        } else if (result && result.productUrl) {
          // Redirect variant: the server-issued URL carries the page's access key
          addNote('Taking you to the details of the ' + modelNumber + ' you asked about\u2026');
          setTimeout(function() {
            window.location.href = result.productUrl;
          }, 2500);
        }
      })
      .catch(function(err) {
        console.error('TAG Heuer form submission error:', err);

        // Show inline error instead of alert
        var errorContainer = document.getElementById('th-submit-error');
        if (errorContainer) {
          errorContainer.textContent = 'Something went wrong. Please try again or contact us at ' + TAG_HEUER_EMAIL_CONFIG.internalRecipient;
          errorContainer.style.display = 'block';
        } else {
          // Fallback alert only if no inline error element exists
          alert('Something went wrong. Please try again or contact us directly.');
        }
      })
      .finally(function() {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.classList.remove('th-inquiry__btn--loading');
        }
      });
    });
  }
}

// Initialize when DOM is ready
export default function initTagHeuerInquiry() {
  new TagHeuerInquiryForm();
}
